import { Component, Inject, OnInit, Optional } from '@angular/core';
import { DateModel } from '@app/core/components/date-range/date-range.component';
import { Item } from '@app/core/components/listbox/listbox.component';
import { TtScreenBlockService } from '@app/core/components/tt-screen-block/tt-screen-block.service';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { SrFollowUpsGet, SrFollowUpService } from '@app/services/sr-follow-up.service';

@Component({
    selector: 'tt-sr-follow-ups',
    templateUrl: './sr-follow-ups.component.html',
    styleUrls: ['./sr-follow-ups.component.css'],
    standalone: false,
})
export class SrFollowUpsComponent implements OnInit {
    public page?: SrFollowUpsGet;
    public items: Item[] = [];
    public error = '';
    private readonly intervals = ['day', 'week', 'month', 'quarter', 'biannual', 'year', 'yeartodate', 'minmax'];
    private readonly rememberTimers = new Map<keyof SrFollowUpsGet, ReturnType<typeof setTimeout>>();

    constructor(private readonly followUp: SrFollowUpService, private readonly remember: RememberService, private readonly screenBlock: TtScreenBlockService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }
    public async ngOnInit(): Promise<void> {
        try { this.page = await this.followUp.getFollowUps(); await this.loadItems(); } catch (error) { this.showError(error); }
    }
    public async onViewChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.gb_srfollowups_main = value;
        this.rememberField('gb_srfollowups_main');
        await this.loadItems();
    }
    public onFilterChanged(value: string): void {
        if (!this.page || value === this.page.filterval) return;
        this.page.filterval = value;
        this.rememberField('filterval');
    }
    public async onSubResourcesChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.show_sub_resources) return;
        this.page.show_sub_resources = value;
        const load = this.loadItems();
        this.rememberField('show_sub_resources');
        await load;
    }
    public async onDatesChanged(dates: DateModel): Promise<void> {
        if (!this.page) return;
        const index = this.intervals.indexOf(dates.dateselector_index);
        const interval = index === -1 ? dates.dateselector_index : String(index + 1);
        const changedFields = (['date_fom', 'date_tom', 'dateselector_index'] as const)
            .filter(field => this.page![field] !== (field === 'dateselector_index' ? interval : dates[field]));
        if (changedFields.length === 0) return;
        const oldDateRange = this.page.date_fomtom;
        this.page = { ...this.page, ...dates, dateselector_index: interval, date_fomtom: dates.date_fom + dates.date_tom };
        changedFields.forEach(field => this.rememberField(field));
        // The legacy watcher reloads only when the concatenated dates change, not for interval-only changes.
        if (oldDateRange !== this.page.date_fomtom) await this.loadItems();
    }
    private async loadItems(): Promise<void> {
        if (!this.page?.gb_srfollowups_main?.trim() || !this.page.date_fom?.trim() || !this.page.date_tom?.trim() ||
            !this.page.dateselector_index?.trim() || this.page.filterval === undefined || this.page.filterval === null) return;
        this.screenBlock.start();
        this.items = [];
        try { this.items = await this.followUp.getFollowUpList(this.page); }
        catch (error) { this.showError(error); }
        finally { this.screenBlock.stop(); }
    }
    private rememberField(field: keyof SrFollowUpsGet): void {
        const timer = this.rememberTimers.get(field);
        if (timer !== undefined) clearTimeout(timer);
        this.rememberTimers.delete(field);
        const value = this.page?.[field];
        if (value === undefined || value === null || (!['filterval', 'show_sub_resources'].includes(field) && !value.trim())) return;
        const write = () => {
            this.rememberTimers.delete(field);
            void this.remember.remember(`w_srfollowups.${field}`, value).catch(error => this.showError(error));
        };
        // Legacy delays every remembered field by two seconds while filterval is nonempty.
        if (this.page!.filterval > '') this.rememberTimers.set(field, setTimeout(write, 2000));
        else write();
    }
    public openItem(event: { item: Item; event: MouseEvent }): void {
        if (!event.item.item_state) return;
        const to = ['srfollowup', 'srfollowupforward', 'srfollowups'].includes(event.item.item_state) ? `${event.item.item_state}-test` : event.item.item_state;
        const target = { to, parms: event.item.item_parms };
        if (event.event.ctrlKey) this.navigator?.newTab(target); else this.navigator?.go(target);
    }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
